import React from 'react';
import styled from 'styled-components';
import {
  Button,
  IconSize,
  IconType,
  IconVariant,
  IconWrap,
  Typography
} from '../../..';
import { theme } from '../../theme/semanticTokens';

export type NotificationVariant =
  | 'success'
  | 'critical'
  | 'warning'
  | 'informational';

export interface NotificationProps {
  variant?: NotificationVariant;
  title: string;
  description?: string;
  onClose?: () => void;
  className?: string;
}

const NotificationContainer = styled.div<{
  $variant: NotificationVariant;
  $hasDescription: boolean;
}>`
  display: flex;
  align-items: ${({ $hasDescription }) =>
    $hasDescription ? 'flex-start' : 'center'};
  gap: ${theme.dime.spacing.l};
  padding: ${theme.dime.spacing.l};
  border-radius: ${theme.dime.radius.m};
  border: 1px solid ${theme.dime.colors.border.static};
  background-color: ${theme.dime.colors.surface.primary};
  box-shadow: 0px 2px 16px 0px rgba(0, 0, 0, 0.2);
  width: 400px;
`;

const IconContainer = styled.div<{ $variant: NotificationVariant }>`
  .icon-wrap {
    color: ${({ $variant }) => {
      switch ($variant) {
        case 'success':
          return theme.dime.colors.icon.success;
        case 'critical':
          return theme.dime.colors.icon.critical;
        case 'warning':
          return theme.dime.colors.icon.warning;
        case 'informational':
          return theme.dime.colors.icon.info;
      }
    }};
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.dime.spacing.m};
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const DescriptionContainer = styled.div`
  display: flex;
  align-items: flex-start;
`;

const CloseButtonContainer = styled.div`
  flex-shrink: 0;
`;

const variantIcons: Record<NotificationVariant, IconType> = {
  success: IconType.Approved,
  critical: IconType.Declined,
  warning: IconType.Warning,
  informational: IconType.Information
};

export const Notification: React.FC<NotificationProps> = ({
  variant = 'success',
  title,
  description,
  onClose,
  className = ''
}) => {
  const iconType = variantIcons[variant];

  return (
    <NotificationContainer
      $variant={variant}
      $hasDescription={!!description}
      className={className}
    >
      <IconContainer $variant={variant}>
        <IconWrap
          type={iconType}
          size={IconSize.Medium}
          variant={IconVariant.Solid}
          className="icon-wrap"
        />
      </IconContainer>
      <ContentContainer>
        <TitleContainer>
          <Typography variant="heading4Bold">{title}</Typography>
        </TitleContainer>
        {!!description && (
          <DescriptionContainer>
            <Typography variant="body1">{description}</Typography>
          </DescriptionContainer>
        )}
      </ContentContainer>
      {!!onClose && (
        <CloseButtonContainer>
          <Button
            variant="secondary"
            iconOnly={IconType.Close}
            size="middle"
            onClick={onClose}
          />
        </CloseButtonContainer>
      )}
    </NotificationContainer>
  );
};
